import { Add, Button, Chevron, Modal } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { DatePicker, Divider, Input, Select } from 'antd'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  type EolFieldConfig,
  type EolSectionConfigItem,
  eolSectionConfig
} from './eolSectionConfig'
import type { EolEntry, EolStructuredSection } from './eolTypes'

const { TextArea } = Input

interface AddSectionModalProps {
  isModalVisible: boolean
  setIsModalVisible: (visible: boolean) => void
  onSave: (section: EolStructuredSection) => void
  existingSections: EolStructuredSection[]
  isEolCarePlan?: boolean
  editingSection?: EolStructuredSection | null
}

const AddSectionModal: React.FC<AddSectionModalProps> = ({
  isModalVisible,
  setIsModalVisible,
  onSave,
  existingSections,
  isEolCarePlan = false,
  editingSection = null
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const availableConfigs = isEolCarePlan
    ? eolSectionConfig
    : eolSectionConfig.filter((c) => c.sectionType === 'custom')
  const [selectedConfig, setSelectedConfig] =
    useState<EolSectionConfigItem | null>(null)
  const [entries, setEntries] = useState<EolEntry[]>([{}])
  const [customLabel, setCustomLabel] = useState('')

  useEffect(() => {
    if (isModalVisible && editingSection) {
      const config = eolSectionConfig.find(
        (c) => c.sectionType === editingSection.sectionType
      )
      if (config) {
        setSelectedConfig(config)
        setEntries(
          editingSection.entries.length > 0 ? [...editingSection.entries] : [{}]
        )
        if (editingSection.sectionType === 'custom') {
          setCustomLabel(editingSection.label)
        }
      }
    }
  }, [isModalVisible, editingSection])

  const handleClose = () => {
    setSelectedConfig(null)
    setEntries([{}])
    setCustomLabel('')
    setIsModalVisible(false)
  }

  const handleSelectSectionType = (config: EolSectionConfigItem) => {
    setSelectedConfig(config)
    setEntries([{}])
    setCustomLabel('')
  }

  const handleBackToTypes = () => {
    if (editingSection) {
      handleClose()
      return
    }
    setSelectedConfig(null)
    setEntries([{}])
  }

  const handleEntryChange = (
    index: number,
    key: string,
    value: string | undefined
  ) => {
    setEntries((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [key]: value }
      return updated
    })
  }

  const handleAddEntry = () => {
    setEntries((prev) => [...prev, {}])
  }

  const handleRemoveEntry = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSave = () => {
    if (!selectedConfig) return

    const nonEmptyEntries = entries.filter((entry) =>
      Object.values(entry).some((v) => v && v.trim() !== '')
    )

    if (nonEmptyEntries.length === 0) return

    const isCustom = selectedConfig.sectionType === 'custom'
    const label =
      isCustom && customLabel.trim() ? customLabel.trim() : selectedConfig.label

    onSave({
      sectionType: selectedConfig.sectionType,
      label,
      entries: nonEmptyEntries
    })
    handleClose()
  }

  const isSectionAtMax = (config: EolSectionConfigItem) => {
    if (config.maxSections === Infinity) return false
    const existing = existingSections.filter(
      (s) => s.sectionType === config.sectionType
    )
    const count =
      editingSection?.sectionType === config.sectionType
        ? existing.length - 1
        : existing.length
    return count >= config.maxSections
  }

  const hasValidEntries = entries.some((entry) =>
    Object.values(entry).some((v) => v && v.trim() !== '')
  )

  const isCustom = selectedConfig?.sectionType === 'custom'
  const isSaveDisabled = !hasValidEntries || (isCustom && !customLabel.trim())

  if (!isModalVisible) return null

  const isEditing = !!editingSection

  return (
    <StyledModal
      noPadding
      isModalVisible={isModalVisible}
      setIsModalVisible={setIsModalVisible}
      onCancel={handleClose}
      onConfirm={selectedConfig ? handleSave : undefined}
      closeButton
      closeOnConfirm={false}
      testId="eol-add-section"
      confirmButtonProps={
        selectedConfig
          ? { disabled: isSaveDisabled, variant: 'primary' }
          : undefined
      }
      localisation={{
        title: selectedConfig
          ? selectedConfig.label
          : t(carePlansConstants.EOL_ADD_SECTION),
        confirmText: selectedConfig
          ? isEditing
            ? t(carePlansConstants.EOL_UPDATE_SECTION)
            : t(carePlansConstants.EOL_SAVE_SECTION)
          : '',
        cancelText: selectedConfig
          ? t(carePlansConstants.EOL_CANCEL)
          : undefined
      }}
    >
      {!selectedConfig && (
        <>
          <SubtitleText>
            {t(carePlansConstants.EOL_ADD_SECTION_SUBTITLE)}
          </SubtitleText>
          <Divider style={{ margin: '12px 0' }} />
          <SectionTypeList>
            {availableConfigs.map((config) => {
              const isDisabled = isSectionAtMax(config)
              return (
                <SectionTypeItem
                  key={config.sectionType}
                  onClick={() => !isDisabled && handleSelectSectionType(config)}
                  $disabled={isDisabled}
                  data-testid={`eol-section-type-${config.sectionType}`}
                >
                  <SectionTypeInfo>
                    <SectionTypeLabel weight="medium">
                      {config.label}
                    </SectionTypeLabel>
                    <SectionTypeDescription>
                      {config.description}
                    </SectionTypeDescription>
                  </SectionTypeInfo>
                  <ChevronIcon>
                    <Chevron direction="right" />
                  </ChevronIcon>
                </SectionTypeItem>
              )
            })}
          </SectionTypeList>
        </>
      )}
      {selectedConfig && (
        <>
          {!isEditing && (
            <BackButton
              onClick={handleBackToTypes}
              data-testid="eol-back-to-types-btn"
            >
              <Chevron direction="left" />{' '}
              {t(carePlansConstants.EOL_BACK_TO_SECTION_TYPES)}
            </BackButton>
          )}
          <Divider style={{ margin: '12px 0' }} />
          <SectionHeader>
            <SectionHeaderInfo>
              <Heading level={4}>{selectedConfig.label}</Heading>
              <Text level={2}>{selectedConfig.description}</Text>
            </SectionHeaderInfo>
          </SectionHeader>
          {isCustom && (
            <FieldContainer style={{ marginTop: 12, marginBottom: 16 }}>
              <FieldLabel weight="medium">
                {t(carePlansConstants.EOL_SECTION_TITLE_LABEL)}{' '}
                <RequiredAsterisk>*</RequiredAsterisk>
              </FieldLabel>
              <Input
                placeholder={t(
                  carePlansConstants.EOL_SECTION_TITLE_PLACEHOLDER
                )}
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                data-testid="eol-custom-title-input"
              />
            </FieldContainer>
          )}
          <EntriesContainer>
            {entries.map((entry, entryIndex) => (
              <EntryCard
                key={entryIndex}
                data-testid={`eol-entry-card-${entryIndex}`}
              >
                {entries.length > 1 && (
                  <RemoveEntryButton
                    onClick={() => handleRemoveEntry(entryIndex)}
                  >
                    {t(carePlansConstants.EOL_REMOVE)}
                  </RemoveEntryButton>
                )}
                {selectedConfig.fields.map((field) => (
                  <FieldContainer key={field.key}>
                    <FieldLabel weight="medium">
                      {field.label} <RequiredAsterisk>*</RequiredAsterisk>
                    </FieldLabel>
                    {renderField(field, entry, entryIndex, handleEntryChange)}
                  </FieldContainer>
                ))}
              </EntryCard>
            ))}
            {(selectedConfig.maxEntries === Infinity ||
              entries.length < selectedConfig.maxEntries) && (
              <AddEntryButton
                type="button"
                variant="text"
                icon={<Add />}
                onClick={handleAddEntry}
                data-testid="eol-add-another-action-btn"
              >
                {t(carePlansConstants.EOL_ADD_ANOTHER_ACTION)}
              </AddEntryButton>
            )}
          </EntriesContainer>
        </>
      )}
    </StyledModal>
  )
}

const renderField = (
  field: EolFieldConfig,
  entry: EolEntry,
  entryIndex: number,
  onChange: (index: number, key: string, value: string | undefined) => void
) => {
  switch (field.type) {
    case 'textarea':
      return (
        <TextArea
          placeholder="Type here ..."
          autoSize={{ minRows: 3 }}
          value={entry[field.key] || ''}
          onChange={(e) => onChange(entryIndex, field.key, e.target.value)}
        />
      )
    case 'date': {
      const raw = entry[field.key]
      const dateValue = raw
        ? dayjs(raw, ['DD-MM-YYYY', 'YYYY-MM-DD'], true).isValid()
          ? dayjs(raw, ['DD-MM-YYYY', 'YYYY-MM-DD'], true)
          : dayjs(raw)
        : undefined
      return (
        <DatePicker
          style={{ width: '100%' }}
          format="DD-MM-YYYY"
          value={dateValue?.isValid() ? dateValue : undefined}
          onChange={(date) =>
            onChange(
              entryIndex,
              field.key,
              date ? date.format('YYYY-MM-DD') : undefined
            )
          }
          placeholder="dd-mm-yyyy"
        />
      )
    }
    case 'select':
      return (
        <Select
          style={{ width: '100%' }}
          placeholder={`Select ${field.label.toLowerCase()}`}
          value={entry[field.key] || undefined}
          onChange={(value) => onChange(entryIndex, field.key, value)}
          options={field.options}
        />
      )
    default:
      return null
  }
}

const StyledModal = styled(Modal)`
  min-width: 600px;
  max-width: 700px;

  .ant-modal-content {
    padding-top: 20px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
  }

  .ant-modal-header {
    padding-bottom: 4px !important;
  }

  .ant-modal-title {
    font-size: 22px !important;
    font-weight: 500 !important;
    text-transform: none !important;
    color: ${({ theme }) => theme.color.neutral.color3} !important;
  }

  .ant-modal-footer {
    margin-top: 0 !important;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }
`

const SubtitleText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-size: 14px;
  margin-bottom: 0;
`

const SectionTypeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 500px;
  overflow-y: auto;
`

const SectionTypeItem = styled.div<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: background-color 0.15s;

  &:hover {
    background-color: ${({ $disabled, theme }) =>
      $disabled ? 'transparent' : theme.color.neutral.color9};
  }
`

const SectionTypeInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
`

const SectionTypeLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color2};
  font-size: 16px;
  margin-bottom: 0;
`

const SectionTypeDescription = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-size: 14px;
  margin-bottom: 0;
`

const ChevronIcon = styled.span`
  color: ${({ theme }) => theme.color.neutral.color5};
  margin-left: 8px;
  display: flex;
  align-items: center;
`

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.color.neutral.color4};
  cursor: pointer;
  font-size: 16px;
  padding: 0;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: ${({ theme }) => theme.color.status.primary};
  }
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  margin-bottom: 16px;
`

const SectionHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  > div:last-child {
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.color.neutral.color4};
  }
`

const EntriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EntryCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  border-radius: 12px;
`

const RemoveEntryButton = styled.button`
  position: absolute;
  top: 8px;
  right: 12px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.color.status.danger};
  cursor: pointer;
  font-size: 13px;
  padding: 0;
`

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FieldLabel = styled(Text)`
  font-size: 16px;
  color: ${({ theme }) => theme.color.neutral.color3};
  margin-bottom: 0;
`

const RequiredAsterisk = styled.span`
  color: ${({ theme }) => theme.color.status.danger};
`

const AddEntryButton = styled(Button)`
  align-self: center;
  width: 100%;
  padding: 16px 0 !important;
  border: 1px dashed ${({ theme }) => theme.color.neutral.color6} !important;
  border-radius: 12px;
  justify-content: center;
`

export default AddSectionModal
